import { Box, HStack, SkeletonText, VStack, Button } from "@chakra-ui/react";
import React from "react";
import { Card } from "../../components/Card/Card";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { MembersTable } from "../../components/Member/MembersTable";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { useSearchInput } from "../../hooks/useSearchInput";

export const Members = () => {
  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search members...",
  });

  const {
    items: members,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedAdminApiRequest(
    (apiClient) => apiClient.getMembers({ q: debouncedQuery }),
    (apiClient, _, continuationToken) =>
      apiClient.getMembers({
        q: debouncedQuery,
        continuationToken,
      }),
    [debouncedQuery]
  );

  const skeletonLoading = (rows: number = 12) => {
    return (
      <HStack width="100%" boxShadow="lg">
        <Box width="22%" padding="4" pr="0">
          <SkeletonText mt="4" noOfLines={rows} spacing="4" />
        </Box>
        <Box width="8%" padding="4" pr="0">
          <SkeletonText mt="4" noOfLines={rows} spacing="4" />
        </Box>
        <Box width="53.5%" pt="4" pb="4">
          <SkeletonText mt="4" noOfLines={rows} spacing="4" />
        </Box>
        <Box width="7%" pt="4" pb="4" pr="4">
          <SkeletonText mt="4" noOfLines={rows} spacing="4" />
        </Box>
        <Box width="9.5%" pt="4" pb="4" pr="4">
          <SkeletonText mt="4" noOfLines={rows} spacing="4" />
        </Box>
      </HStack>
    );
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Members</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <Card w="100%">
          <VStack w="100%" spacing="4" alignItems="start">
            <HStack w="100%" justifyItems="start">
              <Box w="100%" maxW="300px">
                {searchInputElement}
              </Box>
            </HStack>
            <MembersTable
              members={members}
              isLoading={isLoading}
              debouncedQuery={debouncedQuery}
            />
            {fetchNextPage && !isLoading && (
              <Box w="100%">
                {!isLoadingPage && (
                  <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                    Load more...
                  </Button>
                )}
                {isLoadingPage && skeletonLoading(4)}
              </Box>
            )}
          </VStack>
        </Card>
        {error != null && (
          <ErrorMessage>
            An error was encountered while searching members.
          </ErrorMessage>
        )}
      </PageContent>
    </>
  );
};
